import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Server, Webhook, Zap, Clock } from 'lucide-react';

const BackgroundProcessing = () => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-4xl md:text-5xl font-bold text-gray-800 flex items-center gap-3">
          <Server className="text-green-600" size={40} />
          Background Processing
        </h1>
        <p className="text-gray-600 mt-2">Aplikasi bekerja 24/7 tanpa perlu browser terbuka</p>
      </div>

      {/* Overview */}
      <Card className="glass-effect border-2 border-green-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="text-green-600" size={28} />
            Always-On WhatsApp Service
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-lg text-gray-700 leading-relaxed">
            WhatsApp Manager berjalan sebagai <strong>background service</strong> yang aktif 24/7. 
            Anda tidak perlu membuka browser untuk menerima pesan atau mengirim webhook. 
            Semua proses berjalan otomatis di server.
          </p>
        </CardContent>
      </Card>

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Auto Reconnection */}
        <Card className="glass-effect card-hover">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Zap className="text-yellow-600" size={24} />
              Auto Reconnection
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-2">
              <CheckCircle className="text-green-600 mt-1 flex-shrink-0" size={18} />
              <p className="text-gray-700">Service restart otomatis reconnect semua user yang sudah terhubung</p>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="text-green-600 mt-1 flex-shrink-0" size={18} />
              <p className="text-gray-700">WhatsApp sessions dipulihkan dari database</p>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="text-green-600 mt-1 flex-shrink-0" size={18} />
              <p className="text-gray-700">Tidak perlu scan QR code lagi setelah restart</p>
            </div>
          </CardContent>
        </Card>

        {/* Message Processing */}
        <Card className="glass-effect card-hover">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Webhook className="text-blue-600" size={24} />
              Background Message Processing
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-2">
              <CheckCircle className="text-green-600 mt-1 flex-shrink-0" size={18} />
              <p className="text-gray-700">Pesan masuk tetap diterima meskipun browser tertutup</p>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="text-green-600 mt-1 flex-shrink-0" size={18} />
              <p className="text-gray-700">Webhook dikirim otomatis untuk setiap pesan</p>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="text-green-600 mt-1 flex-shrink-0" size={18} />
              <p className="text-gray-700">Semua pesan tersimpan di database</p>
            </div>
          </CardContent>
        </Card>

        {/* Health Monitoring */}
        <Card className="glass-effect card-hover">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Clock className="text-purple-600" size={24} />
              Periodic Health Check
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-2">
              <CheckCircle className="text-green-600 mt-1 flex-shrink-0" size={18} />
              <p className="text-gray-700">Cek koneksi setiap 5 menit otomatis</p>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="text-green-600 mt-1 flex-shrink-0" size={18} />
              <p className="text-gray-700">Auto-reconnect jika koneksi terputus</p>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="text-green-600 mt-1 flex-shrink-0" size={18} />
              <p className="text-gray-700">Self-healing system tanpa intervensi manual</p>
            </div>
          </CardContent>
        </Card>

        {/* Persistent Storage */}
        <Card className="glass-effect card-hover">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Server className="text-green-600" size={24} />
              Persistent Storage
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-2">
              <CheckCircle className="text-green-600 mt-1 flex-shrink-0" size={18} />
              <p className="text-gray-700">WhatsApp auth state tersimpan di MongoDB</p>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="text-green-600 mt-1 flex-shrink-0" size={18} />
              <p className="text-gray-700">Session survive server restart</p>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="text-green-600 mt-1 flex-shrink-0" size={18} />
              <p className="text-gray-700">Tidak ada data hilang saat maintenance</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* How It Works */}
      <Card className="glass-effect">
        <CardHeader>
          <CardTitle className="text-2xl">How It Works</CardTitle>
          <CardDescription>Arsitektur background processing</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex gap-4 items-start">
              <Badge className="bg-green-600 text-lg px-4 py-2">1</Badge>
              <div className="flex-1">
                <h4 className="font-semibold mb-1">Service Startup</h4>
                <p className="text-gray-700">Saat WhatsApp service start, sistem membaca database untuk menemukan semua user yang <code className="bg-gray-200 px-2 py-1 rounded">whatsappConnected = true</code></p>
              </div>
            </div>

            <div className="flex gap-4 items-start">
              <Badge className="bg-green-600 text-lg px-4 py-2">2</Badge>
              <div className="flex-1">
                <h4 className="font-semibold mb-1">Auto Reconnection</h4>
                <p className="text-gray-700">Untuk setiap user yang ditemukan, sistem otomatis membuat koneksi WhatsApp menggunakan auth state yang tersimpan di database. Tidak perlu scan QR lagi.</p>
              </div>
            </div>

            <div className="flex gap-4 items-start">
              <Badge className="bg-green-600 text-lg px-4 py-2">3</Badge>
              <div className="flex-1">
                <h4 className="font-semibold mb-1">Message Listening</h4>
                <p className="text-gray-700">WhatsApp socket untuk setiap user aktif dan listening untuk incoming messages. Ini berjalan independent dari browser.</p>
              </div>
            </div>

            <div className="flex gap-4 items-start">
              <Badge className="bg-green-600 text-lg px-4 py-2">4</Badge>
              <div className="flex-1">
                <h4 className="font-semibold mb-1">Message Processing</h4>
                <p className="text-gray-700">Saat pesan masuk, sistem:</p>
                <ul className="list-disc ml-5 mt-2 space-y-1 text-gray-700">
                  <li>Menyimpan pesan ke database</li>
                  <li>Mengirim webhook ke URL yang dikonfigurasi (jika enabled)</li>
                  <li>Log webhook delivery ke database</li>
                  <li>Emit ke WebSocket (jika ada browser connected)</li>
                </ul>
              </div>
            </div>

            <div className="flex gap-4 items-start">
              <Badge className="bg-green-600 text-lg px-4 py-2">5</Badge>
              <div className="flex-1">
                <h4 className="font-semibold mb-1">Health Monitoring</h4>
                <p className="text-gray-700">Setiap 5 menit, sistem mengecek semua koneksi dan auto-reconnect jika ada yang terputus. Self-healing otomatis.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Benefits */}
      <Card className="glass-effect bg-gradient-to-r from-green-50 to-blue-50">
        <CardHeader>
          <CardTitle className="text-2xl">Benefits</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="text-green-600 mt-1" size={20} />
              <div>
                <h4 className="font-semibold">24/7 Availability</h4>
                <p className="text-sm text-gray-700">Aplikasi bekerja non-stop tanpa perlu browser</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle className="text-green-600 mt-1" size={20} />
              <div>
                <h4 className="font-semibold">Zero Missed Messages</h4>
                <p className="text-sm text-gray-700">Semua pesan masuk pasti terdeteksi dan diproses</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle className="text-green-600 mt-1" size={20} />
              <div>
                <h4 className="font-semibold">Reliable Webhooks</h4>
                <p className="text-sm text-gray-700">Webhook dikirim real-time dengan logging lengkap</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle className="text-green-600 mt-1" size={20} />
              <div>
                <h4 className="font-semibold">Auto Recovery</h4>
                <p className="text-sm text-gray-700">Sistem recover otomatis dari gangguan</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle className="text-green-600 mt-1" size={20} />
              <div>
                <h4 className="font-semibold">Production Ready</h4>
                <p className="text-sm text-gray-700">Siap deploy untuk production workload</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle className="text-green-600 mt-1" size={20} />
              <div>
                <h4 className="font-semibold">Scalable</h4>
                <p className="text-sm text-gray-700">Support multiple users concurrent</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Technical Info */}
      <Card className="glass-effect">
        <CardHeader>
          <CardTitle>Technical Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-gray-700">
            <div className="flex gap-2">
              <span className="font-semibold min-w-[180px]">Service Type:</span>
              <span>Node.js background process managed by Supervisor</span>
            </div>
            <div className="flex gap-2">
              <span className="font-semibold min-w-[180px]">Auto Start:</span>
              <span>Yes, starts automatically on server boot</span>
            </div>
            <div className="flex gap-2">
              <span className="font-semibold min-w-[180px]">Auto Restart:</span>
              <span>Yes, auto-restart on crash</span>
            </div>
            <div className="flex gap-2">
              <span className="font-semibold min-w-[180px]">Health Check:</span>
              <span>Every 5 minutes</span>
            </div>
            <div className="flex gap-2">
              <span className="font-semibold min-w-[180px]">Reconnect Delay:</span>
              <span>2 seconds between users</span>
            </div>
            <div className="flex gap-2">
              <span className="font-semibold min-w-[180px]">Storage:</span>
              <span>MongoDB (persistent auth state & messages)</span>
            </div>
            <div className="flex gap-2">
              <span className="font-semibold min-w-[180px]">Logs Location:</span>
              <span>/var/log/supervisor/whatsapp-service.out.log</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BackgroundProcessing;
